import React from 'react';
import { PortalProvider } from '@gorhom/portal';

import { LocationsMap, LocationWarningModal } from '@order/components';
import { useFeatureFlag } from '@order/LaunchDarkly';

import type { LocationsScreenProps } from '../../navigation/AppNavigation.props';
import { LocationLayout } from './components';
import { useLocationSearchTypePicker } from './hooks';
import { useLocationsScreenData } from './LocationsScreen.data';

export const LocationsScreen = (props: LocationsScreenProps) => {
  const { navigation } = props;
  const data = useLocationsScreenData();

  const {
    locationSearchType,
    locations,
    loading,
    selectedLocation,
    isShownLocationWarningModal,
    setLocationSearchType,
    setSelectedLocation,
    hideLocationWarningModal,
    onLocationWarningPress,
    fetchLocationsBasedOnUserGeolocation,
    userLocation,
    mapOffset,
    region,
    showSearchAreaBtn,
  } = data;

  const isMapsAutoFitBoundsEnabled = useFeatureFlag(
    'XP-3179-short-maps-auto-bound-enabled',
  );
  const isMapsOffsetEnabled = useFeatureFlag(
    'XP-3179-short-maps-offset-enabled',
  );
  const isEmptyRegion = region.lat === 0 && region.lng === 0;

  // The location search type picker is part of the header.
  const shouldDisableSearchControlsWhileLoading = useFeatureFlag(
    'XP-3189-short-disable-search-controls-while-fetching-locations',
  );
  const shouldDisableTypePicker =
    shouldDisableSearchControlsWhileLoading && loading;

  const locationSearchTypePicker = useLocationSearchTypePicker({
    navigation,
    locationSearchType,
    setLocationSearchType,
    setSelectedLocation,
    isDisabled: shouldDisableTypePicker,
  });

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => locationSearchTypePicker,
    });
  }, [navigation, locationSearchTypePicker]);

  // fetch available locations based on user's geolocation on screen mount
  React.useEffect(() => {
    if (userLocation.permissionStatus === 'denied') return;

    void fetchLocationsBasedOnUserGeolocation({
      showWarningIfGeolocationDisabled: false,
    });
  }, [fetchLocationsBasedOnUserGeolocation, userLocation.permissionStatus]);

  return (
    <PortalProvider>
      <LocationLayout
        locationSearchTypePicker={locationSearchTypePicker}
        {...data}
      >
        <LocationsMap
          isLoadingMap={isEmptyRegion}
          isLoadingMapLocations={loading}
          onGeolocateUserBtnPress={fetchLocationsBasedOnUserGeolocation}
          offsetY={mapOffset}
          isAutoFitBoundsEnabled={isMapsAutoFitBoundsEnabled}
          isOffsetEnabled={isMapsOffsetEnabled}
          isSearchAreaBtnEnabled={showSearchAreaBtn}
          {...data}
        />
      </LocationLayout>

      {isShownLocationWarningModal ? (
        <LocationWarningModal
          locations={locations}
          onClose={hideLocationWarningModal}
          onConfirm={onLocationWarningPress}
          restaurantSlug={selectedLocation}
        />
      ) : null}
    </PortalProvider>
  );
};
